// src/pages/PrintOrder.jsx

import React, { useEffect } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import Container from "../components/Container";
import a0 from "../images/banner/a0-01.png";
import Dropzone from "react-dropzone";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { deleteAFile, uploadAFile } from "../app/features/upload/uploadSlice";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import * as yup from "yup";
import { createAPrintOrder } from "../app/features/PrintOrder/printSlice";
import { FaUpload } from "react-icons/fa";
import "./styles/printOrder.css";

// Validation schema
const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email address").required("Email is required"),
  mobile: yup.string().required("Mobile number is required"),
  quantity: yup
    .number()
    .required("Number of copies is required")
    .min(1, "Quantity must be at least 1"),
  instruction: yup.string().required("Instructions are required"),
});

const PrintOrder = () => {
  const dispatch = useDispatch();

  useEffect(() => {
  const handleFocus = (e) => {
    const target = e.target;

    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT"
    ) {
      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 350);
    }
  };

  document.addEventListener("focusin", handleFocus);

  return () => {
    document.removeEventListener("focusin", handleFocus);
  };
}, []);

  const uploadProgress = useSelector((state) => state.upload.uploadProgress);
  const { uploading } = useSelector((state) => state.upload);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      quantity: 1,
      instruction: "",
      uploadedFiles: [],
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      dispatch(
        createAPrintOrder({
          name: values.name,
          email: values.email,
          mobile: values.mobile,
          quantity: values.quantity,
          instruction: values.instruction,
          uploadedFiles: values.uploadedFiles,
        })
      );
      resetForm();
    },
  });

  const handleDeleteFile = async (public_id) => {
    try {
      const result = await dispatch(deleteAFile(public_id)).unwrap();
      if (result) {
        const updatedFiles = formik.values.uploadedFiles.filter(
          (file) => file.public_id !== public_id
        );
        formik.setFieldValue("uploadedFiles", updatedFiles);
        toast.success("File deleted successfully");
      } else {
        throw new Error("Failed to delete file");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      toast.error(errorMessage);
    }
  };

  const handleFileDrop = (acceptedFiles) => {
    const formData = new FormData();
    acceptedFiles.forEach((file) => formData.append("files", file));

    dispatch(uploadAFile(formData))
      .unwrap()
      .then((uploadedFiles) => {
        if (Array.isArray(uploadedFiles)) {
          const updatedFiles = [...formik.values.uploadedFiles, ...uploadedFiles];
          formik.setFieldValue("uploadedFiles", updatedFiles);
          toast.success("File uploaded successfully");
        } else {
          toast.error("Unexpected response format from file upload");
        }
      })
      .catch(() => toast.error("Failed to upload File"));
  };

  return (
    <>
      <Meta title="Order Form" />
      <div className="print-hero">
        <h1 className="print-hero-tittle">Print Now</h1>
        <BreadCrumb title="Order Form" />
      </div>
      

      <Container class1="po-wrap">
        {/* top title bar (like screenshot) */}
        <div className="po-titleBar">
          <div className="po-titleText">PRINT ORDER FORM</div>
        </div>

        <div className="po-card">
          <form onSubmit={formik.handleSubmit} className="po-form">
            {/* ===== TOP ROW: left inputs, right paper card ===== */}
            <div className="po-topRow">
              {/* LEFT */}
              <div className="po-left">
                <div className="po-field">
                  <input
                    type="text"
                    className="po-input"
                    placeholder="Name"
                    name="name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  <div className="po-error">{formik.touched.name && formik.errors.name}</div>
                </div>

                <div className="po-field">
                  <input
                    type="email"
                    className="po-input"
                    placeholder="Email"
                    name="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  <div className="po-error">{formik.touched.email && formik.errors.email}</div>
                </div>

                <div className="po-field">
                  <input
                    type="tel"
                    className="po-input"
                    placeholder="Mobile Number"
                    name="mobile"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.mobile}
                  />
                  <div className="po-error">{formik.touched.mobile && formik.errors.mobile}</div>
                </div>
              </div>

              {/* RIGHT (paper sizes card) */}
              <aside className="po-right">
                <div className="po-paperCard">
                  <div className="po-paperTitle">PAPER SIZES INCLUDE;</div>
                  <img src={a0} alt="Paper sizes" className="po-paperImg" />
                </div>
              </aside>
            </div>

            {/* ===== UPLOAD (full width) ===== */}
            <div className="po-section">
              <h3 className="po-sectionTitle">Upload For Printing</h3>

              <div className="po-uploadBox">
                <Dropzone onDrop={handleFileDrop}>
                  {({ getRootProps, getInputProps }) => (
                    <div className="po-drop" {...getRootProps()}>
                      <input {...getInputProps()} />
                      <FaUpload className="po-upIcon" />
                      <p className="po-upText">
                        {uploading ? "Uploading..." : "Upload File here, on click or drag"}
                      </p>
                    </div>
                  )}
                </Dropzone>

                {uploading && (
                  <div className="po-progress">
                    <div className="po-progressBar" style={{ width: `${uploadProgress}%` }}>
                      {uploadProgress}%
                    </div>
                  </div>
                )}
              </div>

              <p className="po-note">
                The maximum file size is 10mb (Pdf, doc, eps, svg, zipped file)
              </p>

              <div className="po-files">
                {formik.values.uploadedFiles.map((file, index) => (
                  <div className="po-fileRow" key={index}>
                    <p className="po-fileName">{file.fileName}</p>
                    <button
                      type="button"
                      className="po-fileDel"
                      onClick={() => handleDeleteFile(file.public_id)}
                      aria-label="Remove file"
                    >
                      <IoMdClose />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* ===== quantity ===== */}
            <div className="po-section">
              <label className="po-label">Number of Copies</label>
              <input
                className="po-input"
                type="number"
                min={1}
                name="quantity"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.quantity}
              />
              <div className="po-error">
                {formik.touched.quantity && formik.errors.quantity}
              </div>
            </div>

            {/* ===== instructions ===== */}
            <div className="po-section">
              <label className="po-label">Instructions</label>
              <textarea
                className="po-textarea"
                rows={6}
                name="instruction"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.instruction}
              />
              <div className="po-error">
                {formik.touched.instruction && formik.errors.instruction}
              </div>
            </div>

            {/* submit bar like screenshot */}
            <button type="submit" className="po-submit">
              Submit
            </button>
          </form>
        </div>
      </Container>
    </>
  );
};

export default PrintOrder;
